import { useEffect, useRef, useState } from 'react';

import { InformationCircleIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import FeatherIcon from 'feather-icons-react';
import { textWithReferences } from 'helpers/form';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import Alert from 'components/Alert/Alert';

import styles from './Checkbox.module.scss';

export const Checkbox = ({
  name,
  value,
  checked,
  indeterminate = false,
  brand,
  label,
  tooltip,
  large = false,
  align = 'center',
  secondary = false,
  disabled = false,
  textParams = {},
  validators = {},
  register = () => {},
  className,
  style,
  onClick = () => {},
  onChange = () => {},
  modalConfirmation = false,
  modalConfirmationTitle,
  modalConfirmationDescription,
  modalConfirmationConfirmText,
  modalConfirmationCancelText,
  showConfirmationOnCheck = true,
  modalConfirmationType = 'primary',
  onModalConfirm = () => {},
  onModalCancel = () => {},
  dataTestId,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const pendingCheckedStateRef = useRef(null);
  const [isChecked, setIsChecked] = useState(checked);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);

  const registration = typeof register === 'function' ? register(name, { ...validators }) : null;
  const safeRegistration = registration && typeof registration === 'object' ? registration : {};
  const {
    ref: registerFieldRef,
    onChange: registerOnChange,
    onBlur: registerOnBlur,
    ...registerRest
  } = safeRegistration;

  // When react-hook-form's register() supplies onChange, it must be the only change handler —
  // spreading `{...register()}` after explicit props used to override custom onChange; calling
  // both register and prop handlers can double-update form state.
  const useRegisterOnlyForChange = Boolean(registerOnChange);

  const commitChange = newCheckedState => {
    setIsChecked(newCheckedState);
    if (useRegisterOnlyForChange) {
      registerOnChange({
        target: {
          checked: newCheckedState,
          type: 'checkbox',
          name,
          value: value ?? '',
        },
      });
    } else {
      onClick(newCheckedState);
      onChange(newCheckedState);
    }
  };

  const attemptChange = newCheckedState => {
    if (disabled) return;
    if (modalConfirmation && newCheckedState === showConfirmationOnCheck) {
      pendingCheckedStateRef.current = newCheckedState;
      setIsConfirmationModalVisible(true);
      return;
    }
    commitChange(newCheckedState);
  };

  const handleModalConfirm = () => {
    setIsConfirmationModalVisible(false);
    commitChange(pendingCheckedStateRef.current);
    onModalConfirm(pendingCheckedStateRef.current);
    pendingCheckedStateRef.current = null;
  };

  const handleModalCancel = () => {
    setIsConfirmationModalVisible(false);
    pendingCheckedStateRef.current = null;
    onModalCancel();
  };

  const handleKeyDown = event => {
    if (event.key !== ' ') return;
    event.preventDefault();
    attemptChange(!isChecked);
  };

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <div
      style={style}
      className={cn(
        'd-flex',
        `align-items-${align}`,
        'u-padding-bottom--small',
        'u-padding-top--small',
        'checkbox-wrapper',
        { [className]: className },
      )}>
      <input
        {...registerRest}
        ref={el => {
          inputRef.current = el;
          if (typeof registerFieldRef === 'function') {
            registerFieldRef(el);
          } else if (registerFieldRef && 'current' in registerFieldRef) {
            registerFieldRef.current = el;
          }
        }}
        type="checkbox"
        name={name}
        disabled={disabled}
        className={cn(
          isChecked ? styles.checkboxActive : styles.checkbox,
          secondary ? styles.checkboxSecondary : '',
          large ? 'u-margin-top--x-small' : '',
        )}
        value={value}
        checked={isChecked}
        onKeyDown={handleKeyDown}
        onBlur={registerOnBlur}
        onChange={e => attemptChange(e.target.checked)}
        tabIndex={0}
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : isChecked}
        data-testid={dataTestId}
      />
      {typeof label === 'string' ? (
        <span
          className={cn(
            'html-embedded-lists',
            large
              ? 'u-font-size-x-large u-font-weight-500 u-padding-left primary-text checkbox-label'
              : 't-small u-padding-left--small primary-text checkbox-label',
          )}
          dangerouslySetInnerHTML={{
            __html: textWithReferences(label, textParams),
          }}
        />
      ) : (
        <span
          className={cn(
            large
              ? 'u-font-size-x-large u-font-weight-500 u-padding-left primary-text checkbox-label'
              : 't-small u-padding-left--small primary-text checkbox-label',
          )}>
          {label}
        </span>
      )}
      {tooltip && (
        <OverlayTrigger placement="bottom" overlay={<Tooltip>{tooltip}</Tooltip>}>
          <span className={styles.tooltip}>
            <FeatherIcon icon="info" />
          </span>
        </OverlayTrigger>
      )}
      {modalConfirmation && (
        <Alert
          icon={InformationCircleIcon}
          show={isConfirmationModalVisible}
          title={modalConfirmationTitle}
          message={modalConfirmationDescription && DOMPurify.sanitize(modalConfirmationDescription)}
          ok={modalConfirmationConfirmText ?? t('common.confirm')}
          cancel={modalConfirmationCancelText ?? t('common.cancel')}
          onOk={handleModalConfirm}
          onCancel={handleModalCancel}
          brand={brand}
          type={modalConfirmationType}
        />
      )}
    </div>
  );
};

Checkbox.propTypes = {
  name: PropTypes.string,
  value: PropTypes.any,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  tooltip: PropTypes.string,
  validators: PropTypes.object,
  register: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  modalConfirmation: PropTypes.bool,
  modalConfirmationTitle: PropTypes.string,
  modalConfirmationDescription: PropTypes.string,
  modalConfirmationConfirmText: PropTypes.string,
  modalConfirmationCancelText: PropTypes.string,
  showConfirmationOnCheck: PropTypes.bool,
  onModalConfirm: PropTypes.func,
  onModalCancel: PropTypes.func,
  dataTestId: PropTypes.string,
};
